import { Component, OnDestroy, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Observable, Subject, of } from 'rxjs';
import { filter, shareReplay, take, takeUntil } from 'rxjs/operators';
import { ModuleAccessService } from '@services/module-access.service';
import { VisibilityService } from '@services/visibility.service';
import { activeModuleName, moduleLabel, linkPath, linkQuery, ModuleSection, ModuleEntry, accionFilaModulo, headerDesdeModulo } from '@services/module-access.logic';
import { Module } from '@models/module';
import { flujosDeJuicios, flujosDeContratos, flujosDeOficios, flujosDeSolicitudes, hijosDeJuicios, destinoAlAbrirPadre, clicEnPadreAbierto, linkCoincideConUrl, urlPerteneceAHijo, menuAlPulsar, flujoQueQuedaAbierto, EstadoMenu } from '@services/juicio-sidebar.logic';
import { CLAVE_GRUPO_FLUJO, SUITE_PRIMER_MODULO } from '@services/suite-shell.logic';
import { contarFlujosPorModulo, FlujosPorModulo, moduloDeEntrada } from '@services/suite-menu.logic';
import { LexgosignService } from '@services/lexgosign.service';
import { PjudIndicatorsAccessService } from '@app/pjud-indicators/pjud-indicators-access.service';
import { HeaderNavService } from '@layout/header-nav/header-nav.service';
import { Header } from '@layout/header-nav/Header';

/** Módulo con sus subitems ya resueltos, para no recalcularlos en cada change-detection. */
interface ModuleWithChildren {
  module: ModuleEntry;
  children: ModuleEntry[];
}
interface SectionView {
  name: string;
  items: ModuleWithChildren[];
}

/**
 * Lista de módulos de la suite para el sidebar (lámina 22).
 *
 * Primer nivel del panel: se ven los módulos por sección (PRINCIPAL · CONTRATO ·
 * ADMIN) con sus subitems; al entrar en uno, el mismo panel pasa a mostrar el
 * sidebar de ese módulo. Un solo panel con dos estados, no dos columnas.
 */
@Component({
  selector: 'app-suite-modules',
  templateUrl: './suite-modules.component.html',
  styleUrls: ['./suite-modules.component.scss'],
  standalone: false
})
export class SuiteModulesComponent implements OnDestroy {

  private readonly moduleAccess = inject(ModuleAccessService);
  private readonly visibility = inject(VisibilityService);
  private readonly router = inject(Router);
  private readonly headerNav = inject(HeaderNavService);
  private readonly pjudAccess = inject(PjudIndicatorsAccessService);
  private readonly lexgosign = inject(LexgosignService);
  private readonly destroy$ = new Subject<void>();
  /** Firma electrónica encendida en el dominio. Hasta que responda, apagada. */
  private firma = false;
  /** Flujos del cliente por `suitemodule`. null hasta que llegan. */
  private flujosPorModulo: FlujosPorModulo = null;
  /**
   * Links de padre que no bastan con estar en el catálogo: el perfil tiene que
   * poder ver esa ruta. La clave es el href, no el nombre del módulo.
   */
  private readonly accesoPorLink = new Map<string, Observable<boolean>>();
  private readonly accesoResuelto = new Map<string, boolean>();
  private lastHeaderModule: string | null = null;
  /**
   * Si el usuario abrió un agrupador a mano (CLM/juicios/…), el header se queda
   * en ese módulo aunque la URL sea de otra pantalla (p. ej. Legal Analytics).
   * Se suelta al navegar a otro módulo o al entrar a un hijo del flujo.
   */
  private pinHeaderAlAgrupador = false;
  /**
   * Padre que el usuario pulsó. /flows/contratos es el link de Services y
   * también el placeholder de Contratos CLM: la URL sola no distingue cuál
   * resaltar, así que manda el clic.
   */
  private moduloElegido: string | null = null;
  modules: Module[] = [];
  /** Secciones con sus módulos y subitems ya resueltos (no se recalcula en el template). */
  sections: SectionView[] = [];
  /** Flujos de juicio reales del tenant, para poblar GESTIÓN JUICIOS. */
  private flowGroupsJuicios: ModuleEntry[] = [];
  /** Flujos de oficios/reclamos reales del tenant (con etapas), análogo a juicios. */
  private flowGroupsOficios: ModuleEntry[] = [];
  /** Flujo de tareas del tenant, para SOLICITUDES LEGALES. */
  private flowGroupsSolicitudes: ModuleEntry[] = [];
  /** Flujos CLM reales del tenant (con etapas), análogo a juicios. */
  private flowGroupsContratos: ModuleEntry[] = [];
  activeModule: string | null = null;
  /** Módulo con ícono abierto (muestra hijos). Null = todos cerrados. */
  moduloAbierto: string | null = null;
  /** Flujo de juicio abierto en el menú desplegable. Null = todos cerrados. */
  flujoAbierto: string | null = null;
  moduleLabel = moduleLabel;
  linkPath = linkPath;
  linkQuery = linkQuery;

  constructor() {
    const indicadores = this.pjudAccess.canAccess().pipe(shareReplay(1));
    this.accesoPorLink.set('/pjud-indicators', indicadores);
    indicadores.pipe(takeUntil(this.destroy$)).subscribe((permitido) => {
      this.accesoResuelto.set('/pjud-indicators', permitido);
      this.refreshActive();
    });
    this.lexgosign.isEnabled().pipe(takeUntil(this.destroy$)).subscribe((enabled) => {
      this.firma = !!enabled;
      this.reloadModules();
      this.refreshActive();
    });
    this.loadFlowGroups();
    this.reloadModules();
    this.refreshActive();
    // El panel no se reconstruye al navegar entre módulos, así que la marca hay
    // que recalcularla igual que en el topbar. takeUntil corta la suscripción al
    // destruir el componente (router.events nunca completa por sí solo).
    this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd), takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.flujosPorModulo) this.aterrizarDesdeIntro();
        this.refreshActive({ fromNavigation: true });
      });
    // La disponibilidad de algún módulo se resuelve por HTTP y llega después.
    this.moduleAccess.changes$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => { this.reloadModules(); this.refreshActive(); });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Trae los grupos de flujo del tenant y los reparte en las secciones del deck:
   * contratos a CONTRATOS CLM, oficios a OFICIOS Y RECLAMOS, tareas a
   * SOLICITUDES LEGALES y juicios a GESTIÓN JUICIOS, según `suitemodule`.
   */
  private loadFlowGroups() {
    this.visibility.getVisibility({ myRequirements: false })
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        const raw = data?.result ?? data;
        const grupos = Array.isArray(raw) ? raw : [];
        this.flowGroupsJuicios = flujosDeJuicios(grupos);
        this.flowGroupsContratos = flujosDeContratos(grupos);
        this.flowGroupsOficios = flujosDeOficios(grupos);
        this.flowGroupsSolicitudes = flujosDeSolicitudes(grupos);
        this.flujosPorModulo = contarFlujosPorModulo(grupos);
        this.reloadModules();
        this.aterrizarDesdeIntro();
        // Si el usuario expandió el módulo antes de que llegaran los flujos,
        // completar el aterrizaje al primer hijo ahora.
        this.aterrizarAgrupadorAbierto();
        // El interruptor puede haber dejado la URL de un flujo antes de que
        // existieran los hijos. Sin esto el menú no marca el módulo.
        this.refreshActive();
      });
  }

  /**
   * Desde el Intro, `?suite=juicios|oficios|clm|solicitudes` abre ese padre.
   * Con página y acceso va ahí; si no, al primer flujo. Sin query no mueve la URL.
   * `?suite=primero` abre el módulo de entrada (dominio sin Notificaciones).
   */
  private aterrizarDesdeIntro(): void {
    const suite = this.suiteDeLaUrl();
    if (!suite) return;
    const items = this.sections.flatMap((s) => s.items);
    const item = suite === SUITE_PRIMER_MODULO
      ? moduloDeEntrada(items)
      : items.find((i) => i.module.name === suite);
    if (!item) return;
    this.moduloAbierto = item.module.name;
    this.abrirPadre(item);
  }

  /** Completa el panel central si hay un agrupador abierto a mano sin URL de hijo. */
  private aterrizarAgrupadorAbierto(): void {
    if (!this.pinHeaderAlAgrupador || !this.moduloAbierto) return;
    const item = this.sections.flatMap((s) => s.items)
      .find((i) => i.module.name === this.moduloAbierto);
    if (!item) return;
    if (item.children.some((c) => urlPerteneceAHijo(this.router.url, c))) return;
    if (this.estaEnPaginaDelPadre(item)) return;
    this.abrirPadre(item);
  }

  private reloadModules() {
    this.modules = this.moduleAccess.getLauncherModules();
    this.sections = this.moduleAccess.getSuiteSections(this.firma, this.flujosPorModulo).map((s: ModuleSection) => ({
      name: s.name,
      items: s.modules.map((module) => ({
        module,
        // Las secciones de flujos se pueblan con la data REAL del tenant:
        // GESTIÓN JUICIOS con los juicios, OFICIOS Y RECLAMOS con los oficios,
        // SOLICITUDES LEGALES con tareas.
        children: this.childrenDe(module),
      })),
    }));
  }

  /** Subitems de un módulo: flujos reales para juicios/oficios/solicitudes/contratos, catálogo para el resto. */
  private childrenDe(module: ModuleEntry): ModuleEntry[] {
    if (module.name === 'juicios') {
      return hijosDeJuicios(this.flowGroupsJuicios, this.moduleAccess.getSuiteChildren(module));
    }
    if (module.name === 'oficios' && this.flowGroupsOficios.length) {
      return this.flowGroupsOficios;
    }
    if (module.name === 'solicitudes' && this.flowGroupsSolicitudes.length) {
      return this.flowGroupsSolicitudes;
    }
    // CONTRATOS CLM: el subitem "Contratos (flujo)" (clm-flujo) se reemplaza por
    // el/los flujo(s) de contratos REALES del tenant; el resto de subitems
    // (Firma, Templates, Archivador, Alertas) se conservan del catálogo.
    if (module.name === 'clm') {
      const base = this.moduleAccess.getSuiteChildren(module);
      if (!this.flowGroupsContratos.length) return base;
      const resto = base.filter((c) => c.name !== 'clm-flujo');
      return [...this.flowGroupsContratos, ...resto];
    }
    return this.moduleAccess.getSuiteChildren(module);
  }

  /** Fila con ícono: si tiene hijos solo despliega; si no, navega.
   * Los módulos con flujos como hijos siempre despliegan. */
  tieneHijos(item: ModuleWithChildren): boolean {
    if (item.module.suitemodule) return true;
    return accionFilaModulo(item.children.length) === 'desplegar';
  }

  moduloEstaAbierto(item: ModuleWithChildren): boolean {
    return this.moduloAbierto === item.module.name;
  }

  /**
   * Abre los hijos del módulo. Si el padre tiene página, cada clic la muestra
   * de nuevo. Si no tiene, el segundo clic solo cierra el agrupador.
   * Con página y acceso va a esa ruta; si no, cae en el primer flujo.
   */
  toggleModulo(item: ModuleWithChildren): void {
    const yaAbierto = this.moduloAbierto === item.module.name;
    if (yaAbierto && clicEnPadreAbierto(item.module.link) === 'cerrar') {
      this.moduloAbierto = null;
      this.flujoAbierto = null;
      this.pinHeaderAlAgrupador = false;
      return;
    }
    this.moduloAbierto = item.module.name;
    this.flujoAbierto = null;
    this.pinHeaderAlAgrupador = true;
    this.moduloElegido = item.module.name;
    this.activeModule = item.module.name;
    this.lastHeaderModule = null;
    this.actualizarHeader();
    this.abrirPadre(item);
  }

  /**
   * Si el padre trae link y el perfil puede ver esa ruta, abre esa página.
   * Si no trae link, o el perfil no puede verla, cae en el primer flujo.
   */
  private abrirPadre(item: ModuleWithChildren): void {
    this.puedeVerLink(item.module.link).pipe(take(1), takeUntil(this.destroy$)).subscribe((puede) => {
      if (this.moduloAbierto !== item.module.name && item.module.name !== this.suiteDeLaUrl()) return;
      const destino = destinoAlAbrirPadre(item.module.link, puede, item.children);
      this.flujoAbierto = destino.flujo;
      this.moduloAbierto = item.module.name;
      this.moduloElegido = item.module.name;
      this.activeModule = item.module.name;
      this.pinHeaderAlAgrupador = true;
      if (destino.url) this.router.navigateByUrl(destino.url);
      this.actualizarHeader();
    });
  }

  /** La URL ya es el link del padre, y ese link está habilitado para el perfil. */
  private estaEnPaginaDelPadre(item: ModuleWithChildren): boolean {
    const path = this.router.url.split('?')[0];
    const propio = item.module.link?.split('?')[0];
    if (!propio || !this.linkHabilitado(item.module.link)) return false;
    return path === propio || path.startsWith(`${propio}/`);
  }

  /** of(true) si el link no tiene una guarda propia. of(false) si no hay link. */
  private puedeVerLink(link: string | undefined): Observable<boolean> {
    const path = (link || '').split('?')[0];
    if (!path) return of(false);
    return this.accesoPorLink.get(path) ?? of(true);
  }

  private linkHabilitado(link: string | undefined): boolean {
    const path = (link || '').split('?')[0];
    if (!path) return false;
    if (!this.accesoPorLink.has(path)) return true;
    return this.accesoResuelto.get(path) === true;
  }

  private suiteDeLaUrl(): string | null {
    return new URLSearchParams(this.router.url.split('?')[1] ?? '').get('suite');
  }

  /** Marca el padre pulsado y cierra lo que esté abierto en otros módulos y en otros flujos. */
  elegirModulo(item: ModuleWithChildren): void {
    this.aplicarMenu(item, menuAlPulsar({
      modulo: item.module.name,
      flujo: null,
      dejaElModuloAbierto: this.tieneHijos(item),
      cierraElFlujo: false,
    }));
  }

  /** Etapa de un flujo: sigue abierto ese flujo y se cierra cualquier otro agrupador. */
  elegirNieto(item: ModuleWithChildren, child: ModuleEntry): void {
    this.aplicarMenu(item, menuAlPulsar({
      modulo: item.module.name,
      flujo: child.name,
      dejaElModuloAbierto: true,
      cierraElFlujo: false,
    }));
  }

  private aplicarMenu(item: ModuleWithChildren, estado: EstadoMenu): void {
    this.moduloElegido = item.module.name;
    this.pinHeaderAlAgrupador = false;
    this.activeModule = item.module.name;
    this.lastHeaderModule = null;
    this.moduloAbierto = estado.modulo;
    this.flujoAbierto = estado.flujo;
    this.actualizarHeader();
  }

  /** Un flujo de juicio se despliega al pulsar; el resto de subitems navega directo. */
  esMenuDesplegable(child: ModuleEntry): boolean {
    return child.name.startsWith('flow-group-') && !!child.children?.length;
  }

  estaAbierto(child: ModuleEntry): boolean {
    return this.flujoAbierto === child.name;
  }

  /** El hijo o la etapa cuya ruta es la URL actual. */
  hijoSeleccionado(entry: { link?: string }): boolean {
    return linkCoincideConUrl(this.router.url, entry.link);
  }

  /**
   * El flujo queda marcado cuando la URL es de ese flujo y ninguna etapa
   * coincide sola: si hay etapa, la marca es de la etapa.
   */
  flujoSeleccionado(child: ModuleEntry): boolean {
    if (child.children?.some((gc) => this.hijoSeleccionado(gc))) return false;
    return urlPerteneceAHijo(this.router.url, child);
  }

  /**
   * Misma idea que el sidebar de Flows: pulsar el flujo abre o cierra sus
   * visibilidades. Al abrirlo entra a la primera etapa.
   */
  toggleFlujo(item: ModuleWithChildren, child: ModuleEntry): void {
    const cierra = this.flujoAbierto === child.name && this.moduloAbierto === item.module.name;
    this.aplicarMenu(item, menuAlPulsar({
      modulo: item.module.name,
      flujo: child.name,
      dejaElModuloAbierto: true,
      cierraElFlujo: cierra,
    }));
    if (cierra || !child.link) return;
    this.router.navigateByUrl(child.link);
  }

  private refreshActive(opts: { fromNavigation?: boolean } = {}) {
    const porUrl = activeModuleName(this.modules, this.router.url);
    this.activeModule = porUrl;
    const url = this.router.url;
    let marcadoPorHijo = false;
    let flujoDeLaUrl: string | null = null;
    for (const section of this.sections) {
      for (const item of section.items) {
        for (const child of item.children) {
          if (!urlPerteneceAHijo(url, child)) continue;
          // El placeholder de CLM apunta a /flows/contratos, que es el link de
          // Services. No le robes el resaltado a Services salvo que el usuario
          // haya pulsado Contratos CLM.
          if (this.linkLoTieneOtroPadre(child, item) && this.moduloElegido !== item.module.name) {
            continue;
          }
          marcadoPorHijo = true;
          this.moduloAbierto = item.module.name;
          this.activeModule = item.module.name;
          const flujo = flujoQueQuedaAbierto(child, url);
          if (flujo) flujoDeLaUrl = flujo;
        }
      }
    }

    if (!marcadoPorHijo) {
      const detalle = /^\/flows\/requirements\/\d+$/.exec(url.split('?')[0]);
      const grupo = detalle ? this.grupoClasicoGuardado() : null;
      const nombre = grupo ? `flow-group-${grupo}` : null;
      if (nombre) {
        for (const section of this.sections) {
          for (const item of section.items) {
            if (!item.children.some((child) => child.name === nombre)) continue;
            marcadoPorHijo = true;
            this.moduloAbierto = item.module.name;
            this.activeModule = item.module.name;
            flujoDeLaUrl = nombre;
          }
        }
      }
    }

    if (marcadoPorHijo) {
      this.pinHeaderAlAgrupador = false;
      this.flujoAbierto = flujoDeLaUrl;
    } else if (
      opts.fromNavigation
      && porUrl
      && this.pinHeaderAlAgrupador
      && porUrl !== this.moduloAbierto
    ) {
      // Navegó a otro módulo del catálogo (Legal Analytics, Notificaciones…).
      this.pinHeaderAlAgrupador = false;
    } else if (this.pinHeaderAlAgrupador && this.moduloAbierto) {
      this.activeModule = this.moduloAbierto;
    } else if (!porUrl && this.moduloAbierto) {
      this.activeModule = this.moduloAbierto;
    }

    if (this.eleccionSigueValida(url)) {
      this.activeModule = this.moduloElegido;
      // Firma, Templates, Archivador y Alertas viven en Contratos CLM y en CLM Sign
      // con la misma URL. El módulo pulsado sigue abierto.
      this.moduloAbierto = this.moduloElegido;
    }

    if (!marcadoPorHijo && porUrl) {
      const dueno = this.sections.flatMap((section) => section.items)
        .find((entry) => entry.module.name === porUrl);
      const propio = dueno?.module.link?.split('?')[0];
      const path = url.split('?')[0];
      if (dueno && propio && path === propio && this.linkHabilitado(dueno.module.link)) {
        this.moduloAbierto = dueno.module.name;
        this.moduloElegido = dueno.module.name;
        this.activeModule = dueno.module.name;
        this.flujoAbierto = null;
        this.pinHeaderAlAgrupador = true;
      }
    }

    this.actualizarHeader();
  }

  /** Grupo de flujo que la lista clásica dejó anotado, para marcar el detalle. */
  private grupoClasicoGuardado(): number | null {
    try {
      const n = Number(localStorage.getItem(CLAVE_GRUPO_FLUJO));
      return Number.isFinite(n) && n > 0 ? n : null;
    } catch {
      return null;
    }
  }

  /** El padre pulsado sigue siendo el de esta URL (link propio o uno de sus hijos). */
  private eleccionSigueValida(url: string): boolean {
    if (!this.moduloElegido) return false;
    const item = this.sections
      .flatMap((section) => section.items)
      .find((entry) => entry.module.name === this.moduloElegido);
    if (!item) return false;
    const path = url.split('?')[0];
    const propio = item.module.link?.split('?')[0];
    if (propio && (path === propio || path.startsWith(`${propio}/`))) return true;
    return item.children.some((child) => urlPerteneceAHijo(url, child));
  }

  /** El link del hijo es el link propio de otro padre del menú. */
  private linkLoTieneOtroPadre(child: ModuleEntry, dueno: ModuleWithChildren): boolean {
    const path = child.link?.split('?')[0];
    if (!path) return false;
    return this.sections.some((section) =>
      section.items.some((item) =>
        item.module.name !== dueno.module.name && item.module.link?.split('?')[0] === path
      )
    );
  }

  /** El título junto al logo refleja el módulo activo del sidebar. */
  private actualizarHeader(): void {
    if (!this.activeModule) return;
    // getLauncherModules filtra `clm` mientras lexgosign está en noDisponibles.
    // Buscar también en sections.
    const entry =
      this.modules.find((m) => m.name === this.activeModule)
      ?? this.sections.flatMap((s) => s.items).find((i) => i.module.name === this.activeModule)?.module;
    const next = headerDesdeModulo(entry);
    if (!next) return;
    const mostrado = this.headerNav.snapshot()?.name;
    if (this.lastHeaderModule === this.activeModule && mostrado === next.name) return;
    this.lastHeaderModule = this.activeModule;
    this.headerNav.changeOption(new Header(next));
  }
}
